import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import {
  LucideAngularModule,
  ArrowLeftIcon,
  SearchIcon,
  PlusIcon,
  MinusIcon,
  ShoppingCartIcon,
} from 'lucide-angular';
import { MainLayout } from '../../layouts/main-layout';
import { SearchInputComponent } from '../../components/shared/search-input';
import { SearchResultsComponent } from '../../components/shared/search-results';
import { Category } from '../../types/category.types';
import { smartTitle } from '../../utils/pipes/smart-capitalize';
import { CategoriesService } from '../../services/categories.service';
import { CartService } from '../../services/cart.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'allas-search',

  imports: [
    CommonModule,
    LucideAngularModule,
    SearchInputComponent,
    SearchResultsComponent,
  ],
  template: `
    <div class="min-h-screen bg-gray-50">
      <!-- Header con botón de volver -->
      <div class="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div class="px-4 py-3">
          <div class="flex items-center gap-3">
            <button
              (click)="goBack()"
              class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <lucide-icon
                [name]="arrowLeftIcon"
                class="w-5 h-5 text-gray-600"
              ></lucide-icon>
            </button>

            <div class="flex-1">
              <allas-search-input
                [autofocus]="true"
                [searchValue]="searchValue()"
                (searchChange)="onSearchChange($event)"
                (searchSubmit)="onSearchSubmit()"
                placeholder="¿Qué repuesto necesitas?"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Contenido de búsqueda -->
      <div class="p-4">
        <allas-search-results
          [results]="filteredCategories()"
          [searchTerm]="searchValue()"
          [loading]="isLoading()"
          (categoryClick)="onCategoryClick($event)"
          (addToCart)="onAddToCart($event)"
        />
      </div>
    </div>
  `,
})
export class SearchPage implements OnInit {
  readonly arrowLeftIcon = ArrowLeftIcon;

  // Servicios
  private categoriesService = inject(CategoriesService);
  private cartService = inject(CartService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private location = inject(Location);

  // Estado
  searchValue = signal('');
  isLoading = signal(false);

  // Categorías filtradas
  filteredCategories = computed(() => {
    const searchTerm = this.searchValue().toLowerCase().trim();

    if (!searchTerm) {
      return [];
    }

    const results = this.categoriesService.searchCategories(searchTerm);
    return results.slice(0, 20); // Más resultados en full-screen
  });

  ngOnInit() {
    // Obtener query inicial de los parámetros de la URL
    this.route.queryParams.subscribe((params) => {
      if (params['q']) {
        this.searchValue.set(params['q']);
      }
    });
  }

  onSearchChange(value: string): void {
    this.searchValue.set(value);

    // Actualizar URL con el query
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: value || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onSearchSubmit(): void {
    // Implementar búsqueda global si es necesario
    console.log('Búsqueda global:', this.searchValue());
  }

  onCategoryClick(category: Category): void {
    this.router.navigate(['/repuestos', category.slug]);
  }

  onAddToCart(event: { category: Category; quantity: number }): void {
    this.cartService.addItemToCart({
      categoria: event.category,
      cantidad: event.quantity,
    });
    this.notificationService.showSuccess(
      `${event.quantity} ${smartTitle(
        event.category.name
      )} agregado al carrito`,
      'Producto disponible para cotización'
    );
  }

  goBack(): void {
    this.location.back();
  }
}
